/**
 * Ryan O'Dowd
 * 2024-10-28
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  MD3DarkTheme as DarkTheme,
  MD3LightTheme as DefaultTheme,
  Provider as PaperProvider,
} from 'react-native-paper';
import React from 'react';
import getStyles from './styles';
import hooks from '@hooks';

const ThemeProvider = (props: ThemeProviderProps) => {
  const styles = hooks.useStyles(getStyles);

  const mode = hooks.useTypedSelector((state) => state.settingsLightDarkMode);
  const isDarkMode = mode === 'dark';

  const _calculateFontSize = (baseSize) => {
    return baseSize * styles.globalScale;
    // "medium" was originally "normal", so i think there's an issue getting the global scale above; (this requires a redux migration to fix, but i'm worried about other non-allowed values...would be better to have a utility function, or to save the _actual_ scale decimal in redux instead of a string that maps to the scale value)
  };

  const paperTheme = isDarkMode ? DarkTheme : DefaultTheme;
  const theme: any = {
    ...paperTheme,
    dark: isDarkMode,
    version: 3,
    colors: { // @TODO: abstract most of this away...move these into the themes and use onPrimary, onSecondary, etc.
      ...paperTheme.colors,
      primary: isDarkMode ? styles.colors.primaryLight : styles.colors.primary,
      onPrimary: isDarkMode ? styles.colors.primaryExtraDark : styles.colors.white,
      secondary: styles.colors.accentDark,
      onSecondary: styles.colors.white,
      background: isDarkMode ? styles.colors.primaryExtraDark : styles.colors.primaryExtraLight,
      primaryContainer: styles.colors.accent,
      onPrimaryContainer: styles.colors.primaryExtraDark,
      secondaryContainer: styles.colors.accent,
      onSecondaryContainer: styles.colors.primaryExtraDark,
      surface: isDarkMode ? styles.colors.primaryExtraDark : styles.colors.primaryExtraLight,
      onSurface: isDarkMode ? styles.colors.white : styles.colors.primaryExtraDark,
      surfaceVariant: isDarkMode ? styles.colors.primaryExtraDark : styles.colors.primaryExtraLight,
      onSurfaceVariant: isDarkMode ? styles.colors.white : styles.colors.primaryExtraDark,
      selectionColor: styles.colors.accent,
      // @TODO: paper-dates am/pm is a different color
    },
    fonts: { // @TODO: fine-tune
      ...paperTheme.fonts,
      regular: { // @TODO: update
        fontFamily: styles.fonts.primary,
      },
      medium: { // @TODO: update
        fontFamily: styles.fonts.primary,
      },
      light: { // @TODO: update
        fontFamily: styles.fonts.primary,
      },
      thin: { // @TODO: update
        fontFamily: styles.fonts.primary,
      },
      titleSmall: { // @TODO: update
        fontFamily: styles.fonts.secondaryBold,
      },
      titleMedium: { // @TODO: update
        fontFamily: styles.fonts.secondaryBold,
      },
      titleLarge: {
        fontFamily: styles.fonts.secondaryBold,
      },
      headlineSmall: {
        fontFamily: styles.fonts.secondaryBold,
        fontSize: _calculateFontSize(20),
      },
      headlineMedium: {
        fontFamily: styles.fonts.secondaryBold,
        fontSize: _calculateFontSize(22),
      },
      headlineLarge: {
        fontFamily: styles.fonts.secondaryBold,
        fontSize: _calculateFontSize(24),
      },
      displaySmall: { // @TODO: update
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      displayMedium: { // @TODO: update
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      displayLarge: { // @TODO: update
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      labelSmall: { // @TODO: update
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      labelMedium: { // @TODO: update
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      labelLarge: { // @TODO: update
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      bodySmall: {
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(14),
      },
      bodyMedium: {
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(16),
      },
      bodyLarge: { // @TODO: menu items...would prefer this to be bigger
        fontFamily: styles.fonts.primary,
        fontSize: _calculateFontSize(16),
      },
      default: { // @TODO: probably don't need this key
        fontFamily: styles.fonts.primary,
        fontWeight: '400',
      },
    },
  };

  return (
    <PaperProvider theme={theme}>
      {props.children(styles)}
    </PaperProvider>
  );
};

class ThemeProviderProps {
  children: (styles: any) => React.ReactNode;
};

export default ThemeProvider;
